import { useState, useEffect } from 'react';
import { gradesFor } from '@/lib/boardGrades';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { apiGet, apiPost } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Flame, Star, Brain, BookOpen, MessageCircle, LogOut, Trophy, Shield, TrendingUp, RefreshCw, ChevronDown, Crown, Users, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import ParentContactsCard from '@/components/ParentContactsCard';

export default function ProfilePage() {
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [eligibility, setEligibility] = useState(null);
  const [showBoardChange, setShowBoardChange] = useState(false);
  const [boards, setBoards] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [schools, setSchools] = useState([]);
  const [newBoard, setNewBoard] = useState('');
  const [newClass, setNewClass] = useState('');
  const [newState, setNewState] = useState('');
  const [newCity, setNewCity] = useState('');
  const [customCity, setCustomCity] = useState('');
  const [newSchool, setNewSchool] = useState('');
  const [customSchool, setCustomSchool] = useState('');
  const [changing, setChanging] = useState(false);

  useEffect(() => {
    apiGet('/progress').then(setProgress).catch(() => {});
    apiGet('/leaderboard').then(setLeaderboard).catch(() => {});
    apiGet('/profile/board-change-eligibility').then(setEligibility).catch(() => {});
  }, []);

  const openBoardChange = async () => {
    const opts = await apiGet('/onboarding/options');
    setBoards(opts.boards || []);
    setNewBoard(user?.board || '');
    setNewClass(user?.class_level || '');
    setNewState(user?.state || '');
    setNewCity(user?.city || '');
    setNewSchool(user?.school || '');
    setShowBoardChange(true);
    // Pre-populate states list from known Indian states
    setStates(['Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Delhi','Goa','Gujarat','Haryana','Himachal Pradesh','Jammu & Kashmir','Jharkhand','Karnataka','Kerala','Ladakh','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Puducherry','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal','Chandigarh','Andaman & Nicobar','Dadra & Nagar Haveli and Daman & Diu','Lakshadweep']);
    // Load cities for current state
    if (user?.state) {
      apiGet(`/onboarding/cities?state=${encodeURIComponent(user.state)}`).then(d => setCities(d.cities || [])).catch(() => {});
    }
    // Load schools
    loadSchools(user?.state || '', user?.city || '', user?.board || '');
  };

  const loadSchools = (state, city, board) => {
    const params = new URLSearchParams();
    if (state) params.set('state', state);
    if (city) params.set('city', city);
    if (board) params.set('board', board);
    apiGet(`/onboarding/schools?${params.toString()}`).then(d => setSchools(d.schools || [])).catch(() => {});
  };

  const handleNewBoardChange = (board) => {
    setNewBoard(board);
    loadSchools(newState, newCity, board);
  };

  const handleNewStateChange = (state) => {
    setNewState(state);
    setNewCity('');
    setNewSchool('');
    setCities([]);
    setSchools([]);
    apiGet(`/onboarding/cities?state=${encodeURIComponent(state)}`).then(d => setCities(d.cities || [])).catch(() => {});
    loadSchools(state, '', newBoard);
  };

  const handleNewCityChange = (city) => {
    setNewCity(city);
    setNewSchool('');
    setCustomCity('');
    setCustomSchool('');
    if (city === '__other__') {
      // Auto-fallback school to Other as well
      setNewSchool('__other__');
      setSchools([]);
    } else {
      loadSchools(newState, city, newBoard);
    }
  };

  const handleBoardChange = async () => {
    if (!newBoard || !newClass) { toast.error('Please select board and class'); return; }
    setChanging(true);
    try {
      const schoolName = newSchool === '__other__' ? customSchool : newSchool;
      const cityName = newCity === '__other__' ? customCity : newCity;
      await apiPost('/profile/change-board', { board: newBoard, class_level: newClass, school: schoolName, state: newState, city: cityName });
      toast.success('Profile updated!');
      setShowBoardChange(false);
      if (checkAuth) await checkAuth();
      setEligibility(await apiGet('/profile/board-change-eligibility'));
    } catch (e) {
      toast.error(e.message || 'Failed to update');
    }
    setChanging(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 pb-4" data-testid="profile-page">
      {/* Parent contacts (kid safety) */}
      <div className="mb-6">
        <ParentContactsCard />
      </div>

      {/* Profile Card */}
      <Card className="p-6 rounded-3xl border-slate-100 mb-6 animate-slide-up" data-testid="profile-card">
        <div className="flex items-center gap-4 mb-4">
          <Avatar className="w-16 h-16 border-2 border-blue-100">
            <AvatarImage src={user?.picture} alt={user?.name} />
            <AvatarFallback className="bg-blue-50 text-blue-600 font-heading text-xl">
              {user?.name?.[0] || 'U'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <h2 className="font-heading text-xl font-bold text-slate-900">{user?.name}</h2>
            <p className="text-sm text-slate-500 font-body">{user?.email}</p>
            <div className="flex gap-2 mt-1 flex-wrap">
              <Badge className="bg-blue-50 text-blue-600 text-xs">{user?.board}</Badge>
              <Badge className="bg-emerald-50 text-emerald-600 text-xs">Class {user?.class_level}</Badge>
              {user?.school && <Badge className="bg-amber-50 text-amber-600 text-xs">{user.school}</Badge>}
              {user?.role === 'admin' && <Badge className="bg-purple-50 text-purple-600 text-xs"><Shield className="w-3 h-3 mr-1" />Admin</Badge>}
            </div>
          </div>
        </div>
        {user?.subjects?.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {user.subjects.map(s => (
              <Badge key={s} variant="outline" className="text-xs font-body">{s}</Badge>
            ))}
          </div>
        )}
      </Card>

      {/* Board & Class Change */}
      {eligibility && (
        <Card className="p-5 rounded-2xl border-slate-100 mb-6 animate-slide-up" data-testid="board-change-section">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-heading text-sm font-semibold text-slate-700">Education Board & Class</h3>
              <p className="text-xs text-slate-400 font-body mt-0.5">
                {user?.board} &middot; Class {user?.class_level}
              </p>
            </div>
            {eligibility.can_change ? (
              <Button data-testid="change-board-btn" onClick={openBoardChange} variant="outline"
                className="rounded-full text-xs font-heading border-blue-200 text-blue-600 hover:bg-blue-50 gap-1.5">
                <RefreshCw className="w-3.5 h-3.5" /> Change
              </Button>
            ) : (
              <span className="text-xs text-slate-400 font-body">{eligibility.reason}</span>
            )}
          </div>
          {eligibility.can_change && !eligibility.is_admin && (
            <p className="text-[10px] text-slate-400 font-body mt-2">
              One-time change &middot; {eligibility.reason}
            </p>
          )}
          {eligibility.is_admin && (
            <p className="text-[10px] text-purple-500 font-body mt-2">Admin — unlimited changes for testing</p>
          )}

          {showBoardChange && (
            <div className="mt-4 pt-4 border-t border-slate-100 space-y-3" data-testid="board-change-form">
              <select data-testid="new-board-select" value={newBoard} onChange={e => handleNewBoardChange(e.target.value)}
                className="w-full p-2.5 rounded-xl border-2 border-slate-200 bg-white text-sm font-body focus:border-blue-500 focus:outline-none">
                <option value="">Select Education Board</option>
                {boards.map(b => <option key={b.board_id} value={b.name}>{b.name}</option>)}
              </select>
              <select data-testid="new-class-select" value={newClass} onChange={e => setNewClass(e.target.value)}
                className="w-full p-2.5 rounded-xl border-2 border-slate-200 bg-white text-sm font-body focus:border-blue-500 focus:outline-none">
                <option value="">Select Class</option>
                {gradesFor(boards, newBoard).map(g => (
                  <option key={g} value={g}>Class {g}</option>
                ))}
              </select>
              <select data-testid="new-state-select" value={newState} onChange={e => handleNewStateChange(e.target.value)}
                className="w-full p-2.5 rounded-xl border-2 border-slate-200 bg-white text-sm font-body focus:border-blue-500 focus:outline-none">
                <option value="">Select State</option>
                {states.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              {cities.length > 0 && (
                <select data-testid="new-city-select" value={newCity} onChange={e => handleNewCityChange(e.target.value)}
                  className="w-full p-2.5 rounded-xl border-2 border-slate-200 bg-white text-sm font-body focus:border-blue-500 focus:outline-none">
                  <option value="">Select City/District</option>
                  {cities.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                  <option value="__other__">Other - Type your city</option>
                </select>
              )}
              {newCity === '__other__' && (
                <input data-testid="custom-city-input" type="text" value={customCity}
                  onChange={e => setCustomCity(e.target.value)}
                  placeholder="Type your city/district name"
                  className="w-full p-2.5 rounded-xl border-2 border-slate-200 bg-white text-sm font-body focus:border-blue-500 focus:outline-none" />
              )}
              {newCity !== '__other__' && (
                <select data-testid="new-school-select" value={newSchool === '__other__' ? '__other__' : newSchool}
                  onChange={e => { setNewSchool(e.target.value); if (e.target.value !== '__other__') setCustomSchool(''); }}
                  className="w-full p-2.5 rounded-xl border-2 border-slate-200 bg-white text-sm font-body focus:border-blue-500 focus:outline-none">
                  <option value="">Select Educational Institution</option>
                  {schools.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
                  <option value="__other__">Other - Type your school name</option>
                </select>
              )}
              {(newSchool === '__other__' || newCity === '__other__') && (
                <input data-testid="custom-school-input" type="text" value={customSchool}
                  onChange={e => setCustomSchool(e.target.value)}
                  placeholder="Type your school/institution name"
                  className="w-full p-2.5 rounded-xl border-2 border-slate-200 bg-white text-sm font-body focus:border-blue-500 focus:outline-none" />
              )}
              <div className="flex gap-2">
                <Button data-testid="confirm-board-change" onClick={handleBoardChange} disabled={changing}
                  className="flex-1 bg-blue-500 hover:bg-blue-600 text-white rounded-full text-xs font-heading">
                  {changing ? 'Updating...' : 'Confirm Change'}
                </Button>
                <Button data-testid="cancel-board-change" onClick={() => setShowBoardChange(false)} variant="outline"
                  className="rounded-full text-xs font-heading">
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Stats Grid */}
      {progress && (
        <div className="grid grid-cols-2 gap-3 mb-6 stagger-children">
          {[
            { icon: Flame, label: 'Streak', value: `${progress.streak_days} days`, color: 'text-orange-500 bg-orange-50' },
            { icon: Star, label: 'Points', value: progress.total_points, color: 'text-blue-500 bg-blue-50' },
            { icon: Brain, label: 'Quizzes', value: progress.quizzes_completed, color: 'text-emerald-500 bg-emerald-50' },
            { icon: BookOpen, label: 'Flashcards', value: progress.flashcard_sets, color: 'text-amber-500 bg-amber-50' },
            { icon: MessageCircle, label: 'Chat Sessions', value: progress.chat_sessions, color: 'text-purple-500 bg-purple-50' },
            { icon: TrendingUp, label: 'Accuracy', value: `${progress.accuracy}%`, color: 'text-cyan-500 bg-cyan-50' },
          ].map(stat => (
            <Card key={stat.label} className="p-4 rounded-2xl border-slate-100" data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${stat.color}`}>
                  <stat.icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-heading text-lg font-bold text-slate-800">{stat.value}</p>
                  <p className="text-xs text-slate-500 font-body">{stat.label}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Leaderboard Preview */}
      {leaderboard?.enabled && leaderboard.user_rank > 0 && (
        <Card className="p-5 rounded-2xl border-slate-100 mb-6 animate-slide-up" data-testid="leaderboard-preview">
          <div className="flex items-center gap-3 mb-3">
            <Trophy className="w-5 h-5 text-amber-500" />
            <h3 className="font-heading text-sm font-semibold text-slate-700">Leaderboard</h3>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-600 font-body">Your Rank</p>
            <Badge className="bg-amber-50 text-amber-600 font-heading text-lg px-3">#{leaderboard.user_rank}</Badge>
          </div>
          {leaderboard.entries?.slice(0, 5).map((entry, i) => (
            <div key={entry.user_id} className="flex items-center gap-3 mt-3 py-2 border-t border-slate-50">
              <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-heading font-bold ${i < 3 ? 'bg-amber-50 text-amber-600' : 'bg-slate-100 text-slate-500'}`}>
                {i + 1}
              </span>
              <p className="text-sm font-body text-slate-700 flex-1">{entry.name}</p>
              <span className="text-sm font-heading font-semibold text-slate-600">{entry.total_points} pts</span>
            </div>
          ))}
        </Card>
      )}

      {/* Exams — editing the portion and the plan must be reachable from here,
          not only from the sidebar. */}
      <Button data-testid="exams-link" onClick={() => navigate('/exams')} variant="outline"
        className="w-full rounded-full font-heading mb-3 border-fuchsia-200 text-fuchsia-600 hover:bg-fuchsia-50">
        <CalendarDays className="w-4 h-4 mr-2" /> Exam &amp; Study Plan
      </Button>

      {/* Subscription */}
      <Button data-testid="subscription-link" onClick={() => navigate('/subscription')} variant="outline"
        className="w-full rounded-full font-heading mb-3 border-amber-200 text-amber-600 hover:bg-amber-50">
        <Crown className="w-4 h-4 mr-2" /> Subscription Plans
      </Button>

      {/* Parent Dashboard Link */}
      {user?.role === 'parent' && (
        <Button data-testid="parent-dashboard-link" onClick={() => navigate('/parent')} variant="outline"
          className="w-full rounded-full font-heading mb-3 border-blue-200 text-blue-600 hover:bg-blue-50">
          <Users className="w-4 h-4 mr-2" /> Parent Dashboard
        </Button>
      )}

      {/* Admin Link */}
      {user?.role === 'admin' && (
        <Button data-testid="admin-link" onClick={() => navigate('/admin')} variant="outline"
          className="w-full rounded-full font-heading mb-3 border-purple-200 text-purple-600 hover:bg-purple-50">
          <Shield className="w-4 h-4 mr-2" /> Admin Dashboard
        </Button>
      )}

      {/* Logout */}
      <Button data-testid="logout-btn" onClick={handleLogout} variant="ghost"
        className="w-full rounded-full font-heading text-red-500 hover:bg-red-50 hover:text-red-600">
        <LogOut className="w-4 h-4 mr-2" /> Sign Out
      </Button>
    </div>
  );
}
